<template>
  <div>
    <b-modal
      modal-class="client-form-modal"
      body-class="bg-extra-light-gray p-0"
      footer-class="bg-extra-light-gray"
      v-model="editorShown"
      :title="''"
      @ok="onSave()"
      @close="onCancel()"
      size="md-lg"
      centered
      no-close-on-backdrop
      header-bg-variant="primary"
      header-text-variant="white"
      header-class="bg-purple"
    >
      <template v-slot:modal-header>
        <div
          class="w-100 d-flex flex-row justify-content-between align-items-center"
        >
          <h5 class="modal-title">
            New Invoice
          </h5>
          <div class="d-flex align-items-center">
            <button
              type="button"
              aria-label="Close"
              class="close text-white"
              @click="onCancel()"
            >
              ×
            </button>
          </div>
        </div>
      </template>

      <template v-slot:modal-footer>
        <div
          class="b-row d-flex justify-content-between align-items-center w-100"
        >
          <div class="b-col">
            <span class="text-danger text-sm-left">{{ error }}</span>
          </div>
          <div class="b-col text-right mt-1 mr-2">
            <b-button @click="onCancel()" variant="secondary">Cancel</b-button>
            <b-button class="ml-2" variant="primary" @click="onSave()"
              >Create Invoice
            </b-button>
          </div>
        </div>
      </template>

      <div class="position-relative">
        <div class="d-flex">
          <div class="client-form-container position-relative w-100 bg-white">
            <div class="section-body">
              <b-row>
                <b-col class="modal-col">
                  <b-form-group label="Select Patient" class="w-50">
                    <h6 class="mb-0">
                      <eoc-picker
                        :eocs="eocOptions"
                        :async="true"
                        @search="
                          ({ search, loading }) =>
                            fetchEocsOptionsPicker(search, loading)
                        "
                        v-model="patient"
                        @input="changeValue"
                      />
                    </h6>
                  </b-form-group>
                  <b-form-group label="Select Encounters" class="w-100">
                    <h6 class="mb-0">
                      <grid
                        ref="grid"
                        :columns="columns"
                        :data="items"
                        :store="gridConfig.store"
                        :selectionMode="gridConfig.selectionMode"
                        :columnLines="gridConfig.columnLines"
                        :groupFeature="false"
                        :autoHeight="true"
                        :searchFeature="true"
                        :stripeFeature="false"
                      />
                    </h6>
                  </b-form-group>
                </b-col>
              </b-row>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import EocPicker from "../../controls/EocPicker.vue";
import eocServer from "../../../server/eoc";
import Grid from "../../../third_party/grid/Grid.vue";
import billingServer from "../../../server/billing";

export default {
  name: "BillingDocumentNewInvoice",
  components: {
    Grid,
    EocPicker
  },
  props: ["showing"],
  data() {
    return {
      patient: null,
      eocOptions: [],
      gridConfig: {
        minHeight: "20em",
        autoHeight: true,
        selectionMode: {
          row: true,
          checkbox: true,
          rowCheckboxSelection: true,
          showCheckAll: false
        },
        columnLines: false,
        store: {
          // Default sorters
          sorters: [{ field: "dateAndTime", ascending: false }]
        }
      },
      patientEncounterList: [],
      error: ""
    };
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess", "showError"]),
    async fetchEocsOptionsPicker(search, loading) {
      if (this.lastTimeoutEocsOptionsPicker) {
        clearTimeout(this.lastTimeoutEocsOptionsPicker);
        this.lastTimeoutEocsOptionsPicker = null;
      }
      if (search && search.trim().length >= 3) {
        this.eocOptions = [];
      }
      this.lastTimeoutEocsOptionsPicker = setTimeout(async () => {
        if (!search || search.length == 0 || search.length >= 3) {
          if (search.length >= 3) {
            loading(true);
          }

          this.searchingEocs = true;
          const optionsResult = await eocServer.queryList(
            {
              searchPhrase: search,
              eocStatus:
                !search || search.length == 0 ? ["Active", "Pending"] : [],
              locations: this.locationId ? [this.locationId] : [],
              page: 1,
              pageSize: 50
            },
            {
              "Hide-Loading": true
            }
          );

          this.eocOptions = optionsResult.episodesOfCare.map(eoc => ({
            value: eoc.id,
            label: this.formatEocObj(eoc),
            data: eoc
          }));
          this.searchingEocs = false;
          if (search.length >= 3) {
            loading(false);
          }
        }
      }, 1000);
    },
    async fetchEocs() {
      const optionsResult = await eocServer.queryList(
        {
          searchPhrase: "",
          eocStatus: ["Active", "Pending"],
          locationId: [],
          allLocations: true,
          page: 1,
          pageSize: 50
        },
        {
          "Hide-Loading": true
        }
      );
      this.eocOptions = optionsResult.episodesOfCare.map(eoc => ({
        value: eoc.id,
        label: this.formatEocObj(eoc),
        data: eoc
      }));
    },
    async fetchEncounters() {
      if (this.patient == null) {
        this.patientEncounterList = [];
      } else {
        const eocId = this.patient.data.id;
        const data = {
          eocId: [eocId],
          orderBy: "dateAndTime",
          orderByDescending: true,
          assignedToMe: false
        };

        const result = await billingServer.query(data);

        result.encounters.forEach(x => {
          let isSelfPay =
            !x.scheduleEventParticipantInsurances.insurancePrimary &&
            !x.scheduleEventParticipantInsurances.insuranceSecundary &&
            !x.scheduleEventParticipantInsurances.insuranceTertiary;
          if (
            (x.uninvoicedAmount > 0 &&
              (x.copayAmount > 0 || x.deductibleAmount > 0)) ||
            (x.uninvoicedAmount > 0 && isSelfPay)
          ) {
            this.patientEncounterList.push(x);
          }
        });
      }
    },
    changeValue() {
      this.error = "";
      this.patientEncounterList = [];
    },
    async onSave() {
      const selectedRecords = this.$refs.grid.gridInstance.selectedRecords.map(
        x => x.data
      );
      const id = null;
      const encounterIds = selectedRecords.map(r => r.id);
      const episodeOfCareId = this.patient.value;
      this.$emit("createNewInvoice", id, encounterIds, episodeOfCareId);
      this.changeValue();
      this.patient = null;
    },
    onCancel() {
      this.changeValue();
      this.patient = null;
      this.$emit("cancelNewInvoice");
    }
  },
  computed: {
    ...mapState("layout", {
      currentUser: state => state.currentUser
    }),
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    items() {
      return this.patientEncounterList.map(item => {
        return {
          ...item
        };
      });
    },
    columns() {
      const self = this;
      return [
        {
          text: "Date & Time",
          field: "dateAndTime",
          type: "template",
          width: 250,
          editor: false,
          template: data => {
            return this.formatDate(data.record.data.date);
          },
          sortable() {
            return self.filter.orderByDescending ? -1 : 1;
          }
        },
        {
          text: "Details",
          field: "details",
          type: "template",
          width: 600,
          editor: false,
          template: data => {
            let details = "";
            data.record.data.services.forEach(x => {
              details = details + x.name + ", ";
            });
            return details.slice(0, details.length - 2);
          }
        },
        {
          text: "Amount",
          field: "amount",
          type: "template",
          width: 100,
          editor: false,
          template: data => {
            return "$" + data.record.data.uninvoicedAmount;
          }
        }
      ];
    }
  },
  watch: {
    showing: async function(value) {
      if (value) {
        await this.fetchEocs();
      }
    },
    patient: async function(value) {
      if (value != null) {
        await this.fetchEncounters();
      }
    }
  }
};
</script>

<style scoped>
.section-body {
  min-height: 70px;
}

.modal-col {
  margin: 20px;
}

.badge-fixed-size {
  min-width: 101px;
  margin: 0 10px;
}

.add-button {
  max-height: 35px;
  margin: 30px 10px;
}
</style>
